import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMenuScheduling } from '../context/menuSchedulingContext';
import { useAuth } from '../context/authContext';
import { deleteMenuPlan, getOccasion } from '../services/api';
import { PageHeading } from '../components/ui';
import SignedImage from '../components/SignedImage';
import PrintableCookingPlan from '../components/menuScheduling/PrintableCookingPlan';
import { createPrintWindow, showPrintLoading } from '../utils/printWindowManager';
import '../styles/schedule.css';

const Schedule = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { scheduledPlan, setSchedule } = useMenuScheduling();
    const { user } = useAuth();
    
    const [localSchedule, setLocalSchedule] = useState(null);
    const [menuPlanId, setMenuPlanId] = useState(null);
    const [occasionName, setOccasionName] = useState(null);
    const [resizingSegment, setResizingSegment] = useState(null);
    const [resizeEdge, setResizeEdge] = useState(null); // 'left' or 'right'
    const [resizeStartX, setResizeStartX] = useState(0);
    const [resizeStartTime, setResizeStartTime] = useState(0);
    const [draggingSegment, setDraggingSegment] = useState(null);
    const [dragStartX, setDragStartX] = useState(0);
    const [dragStartTime, setDragStartTime] = useState(0);
    const [scale, setScale] = useState(1); // Time scale: 1 = 1px per second
    const timelineRef = useRef(null);
    const [hoveredSegment, setHoveredSegment] = useState(null);
    const [hoverTimeout, setHoverTimeout] = useState(null);
    const [hoverPopupPosition, setHoverPopupPosition] = useState({ x: 0, y: 0 });
    const [pinnedSegments, setPinnedSegments] = useState([]); // Array of { segment, position }
    const [draggingPopup, setDraggingPopup] = useState(null);
    const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
    
    useEffect(() => {
        // Get schedule from location state or context
        const plan = location.state?.schedule || location.state?.scheduledPlan || scheduledPlan;
        const planId = location.state?.menuPlanId;
        const occId = location.state?.occasionId;
        if (plan) {
            setLocalSchedule(plan);
            if (planId) {
                setMenuPlanId(planId);
            }
            // Fetch occasion name if occasionId is provided
            if (occId && user) {
                loadOccasionName(occId);
            }
        } else {
            // If no schedule, redirect back
            navigate('/explore');
        }
    }, [location.state, scheduledPlan, navigate, setSchedule, user]);

    const loadOccasionName = async (occasionId) => {
        if (!user) return;
        try {
            const token = await user.getIdToken();
            const response = await getOccasion(occasionId, token);
            if (response.occasion && response.occasion.name) {
                setOccasionName(response.occasion.name);
            }
        } catch (err) {
            console.error('Error loading occasion name:', err);
        }
    };
    
    // Get unique identifier for a segment
    const getSegmentKey = (seg) => {
        if (!seg) return '';
        return seg.orderKey || `${seg.recipeId}-${seg.stepIndex}-${seg.segmentIndex}`;
    };
    
    // Handle segment resize
    useEffect(() => {
        if (!resizingSegment || !resizeEdge || !localSchedule) return;
        
        const handleMouseMove = (e) => {
            if (!timelineRef.current) return;
            
            const rect = timelineRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const newTime = Math.max(0, Math.floor(x / scale));
            
            const deltaTime = newTime - resizeStartTime;
            const minDuration = 5; // Minimum 5 seconds
            
            let updatedSegments = [...localSchedule.segments];
            const segmentKey = getSegmentKey(resizingSegment);
            const segmentIndex = updatedSegments.findIndex(s => getSegmentKey(s) === segmentKey);
            
            if (segmentIndex === -1) return;
            
            const segment = updatedSegments[segmentIndex];
            
            if (resizeEdge === 'left') {
                const newStartTime = Math.max(0, resizeStartTime + deltaTime);
                const newDuration = segment.endSec - newStartTime;
                
                if (newDuration >= minDuration && newStartTime < segment.endSec) {
                    // Update this segment
                    updatedSegments[segmentIndex] = {
                        ...segment,
                        startSec: newStartTime,
                        durationSec: newDuration
                    };
                    
                    // Push back all segments in the same recipe step that come before this one
                    updatedSegments = updatedSegments.map(seg => {
                        if (seg.recipeId === segment.recipeId && 
                            seg.stepIndex === segment.stepIndex &&
                            seg.segmentIndex < segment.segmentIndex &&
                            seg.endSec > newStartTime) {
                            const shift = seg.endSec - newStartTime;
                            return {
                                ...seg,
                                startSec: Math.max(0, seg.startSec - shift),
                                endSec: Math.max(seg.startSec, seg.endSec - shift)
                            };
                        }
                        return seg;
                    });
                }
            } else { // right edge
                const newEndTime = Math.max(segment.startSec + minDuration, resizeStartTime + deltaTime);
                const newDuration = newEndTime - segment.startSec;
                
                if (newDuration >= minDuration) {
                    // Update this segment
                    updatedSegments[segmentIndex] = {
                        ...segment,
                        endSec: newEndTime,
                        durationSec: newDuration
                    };
                    
                    // Get all segments in the same step, sorted by segmentIndex
                    const sameStepSegments = updatedSegments
                        .filter(s => s.recipeId === segment.recipeId && s.stepIndex === segment.stepIndex)
                        .sort((a, b) => a.segmentIndex - b.segmentIndex);
                    
                    // Push forward all segments in the same recipe step that come after this one
                    updatedSegments = updatedSegments.map(seg => {
                        if (seg.recipeId === segment.recipeId && 
                            seg.stepIndex === segment.stepIndex &&
                            seg.segmentIndex > segment.segmentIndex &&
                            seg.startSec < newEndTime) {
                            const shift = newEndTime - seg.startSec;
                            return {
                                ...seg,
                                startSec: seg.startSec + shift,
                                endSec: seg.endSec + shift
                            };
                        }
                        // Also push forward segments from other recipes that overlap
                        // But only if they're not active segments (to maintain no active overlap constraint)
                        if (seg.recipeId !== segment.recipeId && 
                            seg.startSec < newEndTime && 
                            seg.endSec > segment.startSec) {
                            const shift = newEndTime - seg.endSec;
                            if (shift > 0 && (seg.type === 'inactive' || segment.type === 'inactive')) {
                                return {
                                    ...seg,
                                    startSec: seg.startSec + shift,
                                    endSec: seg.endSec + shift
                                };
                            }
                        }
                        return seg;
                    });
                }
            }
            
            setLocalSchedule({
                ...localSchedule,
                segments: updatedSegments
            });
        };
        
        const handleMouseUp = () => {
            setResizingSegment(null);
            setResizeEdge(null);
            setResizeStartX(0);
            setResizeStartTime(0);
        };
        
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [resizingSegment, resizeEdge, resizeStartTime, scale, localSchedule]);
    
    // Handle segment dragging (moving segments)
    useEffect(() => {
        if (!draggingSegment || !localSchedule) return;
        
        const handleMouseMove = (e) => {
            if (!timelineRef.current) return;
            
            const rect = timelineRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const newStartTime = Math.max(0, Math.floor(x / scale));
            const duration = draggingSegment.endSec - draggingSegment.startSec;
            const newEndTime = newStartTime + duration;
            
            // If dragging an active segment, check for overlaps with other active segments
            if (draggingSegment.type === 'active') {
                // Check if new position would overlap with any other active segment
                const wouldOverlap = localSchedule.segments.some(seg => {
                    if (seg.type !== 'active') return false;
                    const segmentKey = getSegmentKey(seg);
                    const draggingKey = getSegmentKey(draggingSegment);
                    if (segmentKey === draggingKey) return false; // Skip self
                    
                    // Check for overlap: new segment overlaps if it starts before other ends AND ends after other starts
                    return (newStartTime < seg.endSec && newEndTime > seg.startSec);
                });
                
                if (wouldOverlap) {
                    return; // Don't allow the move if it would create an overlap
                }
            }
            
            // Update the segment position
            const updatedSegments = localSchedule.segments.map(seg => {
                const segmentKey = getSegmentKey(seg);
                const draggingKey = getSegmentKey(draggingSegment);
                
                if (segmentKey === draggingKey) {
                    return {
                        ...seg,
                        startSec: newStartTime,
                        endSec: newEndTime
                    };
                }
                return seg;
            });
            
            setLocalSchedule({
                ...localSchedule,
                segments: updatedSegments
            });
        };
        
        const handleMouseUp = () => {
            setDraggingSegment(null);
            setDragStartX(0);
            setDragStartTime(0);
        };
        
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [draggingSegment, dragStartTime, scale, localSchedule, getSegmentKey]);
    
    // Handle popup dragging
    useEffect(() => {
        if (!draggingPopup) return;
        
        const handleMouseMove = (e) => {
            const pinIndex = pinnedSegments.findIndex(p => getSegmentKey(p.segment) === draggingPopup);
            if (pinIndex >= 0) {
                const newPosition = {
                    x: e.clientX - dragStartPos.x,
                    y: e.clientY - dragStartPos.y
                };
                const updatedPins = [...pinnedSegments];
                updatedPins[pinIndex] = {
                    ...updatedPins[pinIndex],
                    position: newPosition
                };
                setPinnedSegments(updatedPins);
            }
        };
        
        const handleMouseUp = () => {
            setDraggingPopup(null);
            setDragStartPos({ x: 0, y: 0 });
        };
        
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [draggingPopup, dragStartPos, pinnedSegments]);
    
    // Cleanup hover timeout on unmount
    useEffect(() => {
        return () => {
            if (hoverTimeout) {
                clearTimeout(hoverTimeout);
            }
        };
    }, [hoverTimeout]);
    
    if (!localSchedule) {
        return <div>Loading schedule...</div>;
    }
    
    // Get recipe objects from schedule if available
    const scheduleRecipes = localSchedule.recipes || [];
    const recipeDataMap = new Map();
    scheduleRecipes.forEach(recipe => {
        recipeDataMap.set(recipe.id || recipe.recipeId, recipe);
    });
    
    // Helper to get step text from recipe data
    const getStepText = (recipeId, stepIndex) => {
        const recipeData = recipeDataMap.get(recipeId);
        if (!recipeData) return null;
        
        // Try to get instructions from recipe data
        let instructions = recipeData.instructions;
        if (typeof instructions === 'string') {
            try {
                instructions = JSON.parse(instructions);
            } catch (e) {
                return null;
            }
        }
        
        const steps = instructions?.steps || [];
        if (stepIndex >= 0 && stepIndex < steps.length) {
            const step = steps[stepIndex];
            return typeof step === 'string' ? step : (step?.text || step?.title || step);
        }
        return null;
    };
    
    // Group segments by recipe for display
    const recipesMap = {};
    for (const segment of localSchedule.segments) {
        const recipeId = segment.recipeId;
        
        if (!recipesMap[recipeId]) {
            const recipeData = recipeDataMap.get(recipeId);
            recipesMap[recipeId] = {
                recipeId,
                recipeName: segment.recipeName,
                recipeImage: recipeData?.images?.[0] || null,
                recipeCategory: recipeData?.category || null,
                segments: []
            };
        }
        
        recipesMap[recipeId].segments.push(segment);
    }
    
    // Convert to array and sort segments by start time, then group by step
    const recipes = Object.values(recipesMap).map(recipe => {
        // Sort segments by start time, then by step index, then by segment index
        const sortedSegments = recipe.segments.sort((a, b) => {
            if (a.startSec !== b.startSec) {
                return a.startSec - b.startSec;
            }
            // If same start time, sort by step index, then segment index
            const aStepIdx = a.stepIndex ?? 0;
            const bStepIdx = b.stepIndex ?? 0;
            if (aStepIdx !== bStepIdx) {
                return aStepIdx - bStepIdx;
            }
            return (a.segmentIndex ?? 0) - (b.segmentIndex ?? 0);
        });
        
        // Group segments by step for visual grouping
        const segmentsByStep = {};
        sortedSegments.forEach(seg => {
            const stepIndex = seg.stepIndex ?? 0;
            if (!segmentsByStep[stepIndex]) {
                segmentsByStep[stepIndex] = {
                    stepIndex,
                    stepTitle: seg.stepTitle || `Step ${stepIndex + 1}`,
                    segments: []
                };
            }
            segmentsByStep[stepIndex].segments.push(seg);
        });
        
        const steps = Object.values(segmentsByStep).sort((a, b) => a.stepIndex - b.stepIndex);
        
        return {
            ...recipe,
            segments: sortedSegments, // Use ALL sorted segments
            steps: steps
        };
    });
    
    const maxTime = localSchedule.makespanSec || Math.max(...localSchedule.segments.map(s => s.endSec || 0));
    
    // Calculate day breaks for long schedules (12+ hours)
    const SECONDS_PER_DAY = 24 * 60 * 60;
    const SECONDS_PER_HOUR = 60 * 60;
    const dayBreaks = [];
    if (maxTime > 12 * SECONDS_PER_HOUR) {
        // Break into days
        let currentDay = 0;
        while (currentDay * SECONDS_PER_DAY < maxTime) {
            dayBreaks.push(currentDay * SECONDS_PER_DAY);
            currentDay++;
        }
    }
    
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };
    
    const formatTimeWithDay = (seconds) => {
        if (dayBreaks.length === 0) {
            return formatTime(seconds);
        }
        const day = Math.floor(seconds / SECONDS_PER_DAY);
        const daySeconds = seconds % SECONDS_PER_DAY;
        const hours = Math.floor(daySeconds / 3600);
        const mins = Math.floor((daySeconds % 3600) / 60);
        return `Day ${day + 1} ${hours}:${mins.toString().padStart(2, '0')}`;
    };
    
    const formatDuration = (seconds) => {
        if (!seconds || seconds === 0) return '0m';
        if (seconds < 60) {
            return `${seconds}s`;
        }
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        
        if (hours > 0) {
            return secs > 0 ? `${hours}h ${mins}m ${secs}s` : `${hours}h ${mins}m`;
        }
        return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
    };
    
    // Handle segment resize start
    const handleResizeStart = (e, segment, edge) => {
        e.stopPropagation();
        e.preventDefault();
        
        setResizingSegment(segment);
        setResizeEdge(edge);
        setResizeStartX(e.clientX);
        setResizeStartTime(edge === 'left' ? segment.startSec : segment.endSec);
    };
    
    // Handle segment drag start (for moving segments)
    const handleSegmentDragStart = (e, segment) => {
        // Don't start drag if clicking on resize handle
        if (e.target.closest('.schedule-segment-resize-handle')) {
            return;
        }
        
        e.stopPropagation();
        e.preventDefault();
        
        setDraggingSegment(segment);
        setDragStartX(e.clientX);
        setDragStartTime(segment.startSec);
    };
    
    const handlePrintPlan = () => {
        // Prepare recipes with full data for printing
        const recipesForPrint = recipes.map(recipe => {
            // Get full recipe data from scheduleRecipes if available
            const fullRecipe = scheduleRecipes.find(r => 
                (r.id || r.recipeId) === recipe.recipeId
            );
            
            return {
                ...recipe,
                ...fullRecipe,
                name: fullRecipe?.name || fullRecipe?.title || recipe.recipeName,
                recipeName: recipe.recipeName,
                ingredients: fullRecipe?.ingredients || [],
                instructions: fullRecipe?.instructions || {},
                category: fullRecipe?.category || recipe.recipeCategory
            };
        });
        
        // Create print window
        const printWindow = createPrintWindow(
            PrintableCookingPlan,
            {
                scheduledPlan: localSchedule,
                recipes: recipesForPrint
            },
            `Cooking Plan - ${recipes.map(r => r.recipeName || r.name).join(', ')}`
        );
        
        if (!printWindow) {
            alert('Please allow popups to print the plan.');
            return;
        }
        
        // Focus the print window
        printWindow.focus();
    };
    
    const handleDeleteSchedule = async () => {
        if (!window.confirm('Are you sure you want to delete this schedule? This will also delete the saved menu plan if one exists.')) {
            return;
        }
        
        try {
            const token = await user.getIdToken();
            
            // If there's a saved menu plan, delete it
            if (menuPlanId) {
                await deleteMenuPlan(menuPlanId, token);
            }
            
            // Navigate back to home or activity page
            navigate('/activity');
        } catch (error) {
            console.error('Error deleting schedule:', error);
            alert('Failed to delete schedule');
        }
    };
    
    return (
        <div className="schedule-page bg-neutral-50 pt-8">
            <PageHeading containerClass="!mb-2">
                <div className="schedule-header">
                    <div>
                        <h1 className="font-sans text-hero text-neutral-900 tracking-tight font-medium">
                            {occasionName ? `${occasionName.toUpperCase()} SCHEDULE` : 'MENU SCHEDULE'}
                        </h1>
                        <p className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60 mt-2">
                            Total Time: {formatTime(maxTime)} • {recipes.length} {recipes.length === 1 ? 'Recipe' : 'Recipes'}
                        </p>
                    </div>
                    <div className="schedule-actions">
                        <button
                            onClick={handleDeleteSchedule}
                            className="px-3 h-10 bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-50 transition-colors font-mono text-xs uppercase tracking-wider"
                            style={{ color: '#DC2626' }}
                        >
                            DELETE
                        </button>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handlePrintPlan();
                            }}
                            className="px-3 h-10 bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-50 transition-colors font-mono text-xs uppercase tracking-wider"
                        >
                            PRINT PLAN
                        </button>
                    </div>
                </div>
            </PageHeading>
            
            <div className="schedule-container">
                {/* Time scale controls and schedule info */}
                <div className="schedule-controls">
                    <div className="schedule-controls-left">
                        <label className="font-mono text-xs uppercase tracking-wider">
                            Scale: 
                            <select 
                                value={scale} 
                                onChange={(e) => setScale(parseFloat(e.target.value))}
                                className="schedule-scale-select"
                            >
                                <option value="0.5">0.5x (Compact)</option>
                                <option value="1">1x (Normal)</option>
                                <option value="2">2x (Detailed)</option>
                                <option value="4">4x (Very Detailed)</option>
                            </select>
                        </label>
                    </div>
                    
                    <div className="schedule-info">
                        {/* Total Time */}
                        <div className="schedule-info-item">
                            <span className="schedule-info-label font-mono text-xs uppercase tracking-wider">Total Time:</span>
                            <span className="schedule-info-value font-mono text-sm">
                                {formatDuration(localSchedule.makespanSec || 0)}
                            </span>
                        </div>
                        
                        {/* Active Time */}
                        <div className="schedule-info-item">
                            <span className="schedule-info-label font-mono text-xs uppercase tracking-wider">Active:</span>
                            <span className="schedule-info-value font-mono text-sm">
                                {formatDuration(localSchedule.activeTotalSec || 0)}
                            </span>
                        </div>
                        
                        {/* Inactive Time */}
                        <div className="schedule-info-item">
                            <span className="schedule-info-label font-mono text-xs uppercase tracking-wider">Inactive:</span>
                            <span className="schedule-info-value font-mono text-sm">
                                {formatDuration(localSchedule.inactiveTotalSec || 0)}
                            </span>
                        </div>
                        
                        {/* Number of Recipes */}
                        <div className="schedule-info-item">
                            <span className="schedule-info-label font-mono text-xs uppercase tracking-wider">Recipes:</span>
                            <span className="schedule-info-value font-mono text-sm">
                                {recipes.length}
                            </span>
                        </div>
                        
                        {/* Total Steps */}
                        {(() => {
                            const totalSteps = recipes.reduce((sum, r) => sum + (r.steps?.length || 0), 0);
                            return totalSteps > 0 ? (
                                <div className="schedule-info-item">
                                    <span className="schedule-info-label font-mono text-xs uppercase tracking-wider">Steps:</span>
                                    <span className="schedule-info-value font-mono text-sm">
                                        {totalSteps}
                                    </span>
                                </div>
                            ) : null;
                        })()}
                        
                        {/* Prerequisites */}
                        {(() => {
                            const allPrerequisites = [];
                            scheduleRecipes.forEach(recipe => {
                                if (recipe.instructions?.prerequisites && Array.isArray(recipe.instructions.prerequisites)) {
                                    recipe.instructions.prerequisites.forEach(prereq => {
                                        if (!allPrerequisites.includes(prereq)) {
                                            allPrerequisites.push(prereq);
                                        }
                                    });
                                }
                            });
                            
                            return allPrerequisites.length > 0 ? (
                                <div className="schedule-info-item schedule-info-prerequisites">
                                    <span className="schedule-info-label font-mono text-xs uppercase tracking-wider">Prerequisites:</span>
                                    <span className="schedule-info-value font-mono text-xs">
                                        {allPrerequisites.join(', ')}
                                    </span>
                                </div>
                            ) : null;
                        })()}
                    </div>
                </div>
                
                {/* Scrollable timeline container */}
                <div className="schedule-timeline-container" ref={timelineRef}>
                    {/* Timeline header */}
                    <div className="schedule-timeline-header" style={{ width: `${200 + (maxTime * scale)}px`, minWidth: `${200 + (maxTime * scale)}px` }}>
                        <div className="schedule-recipe-header">Recipe</div>
                        <div className="schedule-timeline-scroll">
                            <div className="schedule-timeline-ruler" style={{ width: `${maxTime * scale}px`, minWidth: `${maxTime * scale}px` }}>
                                {/* Day break markers */}
                                {dayBreaks.map((dayBreak, idx) => (
                                    <div
                                        key={`day-break-${idx}`}
                                        className="schedule-day-break-marker"
                                        style={{ left: `${dayBreak * scale}px` }}
                                    >
                                        <div className="schedule-day-break-line" />
                                        <span className="schedule-day-break-label">Day {idx + 1}</span>
                                    </div>
                                ))}
                                
                                {/* Collect all step end times and render markers */}
                                {(() => {
                                    // Collect all segment end times (these are step ends)
                                    const stepEndTimes = new Set();
                                    Object.values(recipesMap).forEach(recipe => {
                                        recipe.segments?.forEach(seg => {
                                            if (seg.endSec !== undefined) {
                                                stepEndTimes.add(seg.endSec);
                                            }
                                        });
                                    });
                                    const stepEndTimesArray = Array.from(stepEndTimes).sort((a, b) => a - b);
                                    
                                    // Generate 15-minute interval markers (900 seconds = 15 minutes)
                                    const fifteenMinuteIntervals = [];
                                    for (let time = 0; time <= maxTime; time += 900) {
                                        fifteenMinuteIntervals.push(time);
                                    }
                                    
                                    // Combine markers: 15-minute ticks + step end labels
                                    const allMarkers = new Map();
                                    
                                    // Add all 15-minute interval tick marks (as ticks, labels only if they match step ends)
                                    fifteenMinuteIntervals.forEach(time => {
                                        const isStepEnd = stepEndTimesArray.some(endTime => Math.abs(time - endTime) < 5); // 5 second tolerance
                                        allMarkers.set(time, { time, showLabel: isStepEnd });
                                    });
                                    
                                    // Add step end times with labels (even if not on 15-minute intervals)
                                    stepEndTimesArray.forEach(time => {
                                        const isFifteenMin = fifteenMinuteIntervals.some(interval => Math.abs(time - interval) < 5);
                                        if (!isFifteenMin) {
                                            // This step end is not on a 15-minute mark, add it with label
                                            allMarkers.set(time, { time, showLabel: true });
                                        } else {
                                            // Already added as 15-minute mark, ensure it has label
                                            allMarkers.set(time, { time, showLabel: true });
                                        }
                                    });
                                    
                                    // Render markers
                                    return Array.from(allMarkers.values())
                                        .sort((a, b) => a.time - b.time)
                                        .map((marker, idx) => {
                                            const time = marker.time;
                                            // Skip markers that are too close to day breaks
                                            const tooCloseToDayBreak = dayBreaks.some(db => Math.abs(time - db) < 300);
                                            if (tooCloseToDayBreak) return null;
                                            
                                            return (
                                                <div 
                                                    key={`marker-${time}-${idx}`} 
                                                    className="schedule-timeline-marker"
                                                    style={{ left: `${time * scale}px` }}
                                                >
                                                    {marker.showLabel && (
                                                        <span className="schedule-timeline-label">{formatTime(time)}</span>
                                                    )}
                                                </div>
                                            );
                                        });
                                })()}
                            </div>
                        </div>
                    </div>
                    
                    {/* Recipe rows */}
                    <div className="schedule-recipes">
                    {recipes.map((recipe) => {
                        // Group segments by step for visual brackets
                        // Get all unique step indices from segments
                        const stepIndices = [...new Set(recipe.segments.map(seg => seg.stepIndex ?? 0))].sort((a, b) => a - b);
                        
                        const stepGroups = stepIndices.map(stepIndex => {
                            const stepSegments = recipe.segments.filter(
                                seg => (seg.stepIndex ?? 0) === stepIndex
                            );
                            if (stepSegments.length === 0) return null;
                            
                            const minStart = Math.min(...stepSegments.map(s => s.startSec));
                            const maxEnd = Math.max(...stepSegments.map(s => s.endSec));
                            
                            // Find step title from recipe.steps or use default
                            const stepInfo = recipe.steps.find(s => s.stepIndex === stepIndex);
                            const stepTitle = stepInfo?.stepTitle || `Step ${stepIndex + 1}`;
                            
                            return {
                                step: { stepIndex, stepTitle },
                                segments: stepSegments,
                                startSec: minStart,
                                endSec: maxEnd
                            };
                        }).filter(Boolean);
                        
                        return (
                            <div key={recipe.recipeId} className="schedule-recipe-row hairline" style={{ width: `${200 + (maxTime * scale)}px`, minWidth: `${200 + (maxTime * scale)}px` }}>
                                <div className="schedule-recipe-name">
                                    <div className="schedule-recipe-name-content">
                                        {recipe.recipeImage && (
                                            <div className="schedule-recipe-image">
                                                <SignedImage
                                                    src={recipe.recipeImage}
                                                    alt={recipe.recipeName}
                                                    className="schedule-recipe-image-img"
                                                    category={recipe.recipeCategory}
                                                />
                                            </div>
                                        )}
                                        <span className="font-sans text-base font-medium text-1b263b">
                                            {recipe.recipeName}
                                        </span>
                                    </div>
                                </div>
                                <div className="schedule-recipe-timeline">
                                    <div 
                                        className="schedule-timeline-track"
                                        style={{ width: `${maxTime * scale}px`, minWidth: `${maxTime * scale}px` }}
                                    >
                                        {/* Day break lines in recipe rows */}
                                        {dayBreaks.map((dayBreak, idx) => (
                                            <div
                                                key={`day-break-row-${recipe.recipeId}-${idx}`}
                                                className="schedule-day-break-line-row"
                                                style={{ left: `${dayBreak * scale}px` }}
                                            />
                                        ))}
                                        {/* Step grouping brackets */}
                                        {stepGroups.map((group) => {
                                            const bracketLeft = group.startSec * scale;
                                            const bracketWidth = (group.endSec - group.startSec) * scale;
                                            
                                            return (
                                                <div
                                                    key={`bracket-${recipe.recipeId}-${group.step.stepIndex}`}
                                                    className="schedule-step-bracket"
                                                    style={{
                                                        left: `${bracketLeft}px`,
                                                        width: `${bracketWidth}px`
                                                    }}
                                                >
                                                    <div className="schedule-step-bracket-top" />
                                                    <div className="schedule-step-bracket-bottom" />
                                                </div>
                                            );
                                        })}
                                        
                                        {/* All segments for this recipe */}
                                        {recipe.segments.map((segment) => {
                                            const width = (segment.endSec - segment.startSec) * scale;
                                            const left = segment.startSec * scale;
                                            const isActive = segment.type === 'active';
                                            const duration = segment.endSec - segment.startSec;
                                            const timerLabel = segment.label || (isActive ? 'Active' : 'Inactive');
                                            const segmentKey = getSegmentKey(segment);
                                            
                                            // Find the step this segment belongs to
                                            const stepInfo = recipe.steps.find(s => (s.stepIndex ?? 0) === (segment.stepIndex ?? 0));
                                            const stepTitle = stepInfo?.stepTitle || `Step ${(segment.stepIndex ?? 0) + 1}`;
                                            
                                            const isDragging = draggingSegment && getSegmentKey(draggingSegment) === segmentKey;
                                            
                                            const isHovered = hoveredSegment && getSegmentKey(hoveredSegment) === segmentKey;
                                            const isPinned = pinnedSegments.some(p => getSegmentKey(p.segment) === segmentKey);
                                            
                                            return (
                                                <div
                                                    key={segmentKey}
                                                    data-segment-key={segmentKey}
                                                    className={`schedule-segment ${isActive ? 'schedule-segment-active' : 'schedule-segment-inactive'} ${isDragging ? 'schedule-segment-dragging' : ''}`}
                                                    style={{
                                                        left: `${left}px`,
                                                        width: `${width}px`,
                                                        cursor: isDragging ? 'grabbing' : 'grab',
                                                        opacity: isDragging ? 0.7 : 1
                                                    }}
                                                    onMouseDown={(e) => handleSegmentDragStart(e, segment)}
                                                    onMouseEnter={(e) => {
                                                        if (!isDragging && !draggingSegment) {
                                                            // Clear any existing timeout
                                                            if (hoverTimeout) {
                                                                clearTimeout(hoverTimeout);
                                                            }
                                                            // Set new timeout for 1 second delay
                                                            // Store segmentKey to query element later
                                                            const timeout = setTimeout(() => {
                                                                // Query the element by data attribute instead of using event
                                                                const element = document.querySelector(`[data-segment-key="${segmentKey}"]`);
                                                                if (element) {
                                                                    try {
                                                                        const rect = element.getBoundingClientRect();
                                                                        setHoverPopupPosition({
                                                                            x: rect.left + rect.width / 2,
                                                                            y: rect.top - 10
                                                                        });
                                                                        setHoveredSegment(segment);
                                                                    } catch (err) {
                                                                        console.warn('Error getting element position:', err);
                                                                    }
                                                                }
                                                            }, 1000);
                                                            setHoverTimeout(timeout);
                                                        }
                                                    }}
                                                    onMouseLeave={() => {
                                                        // Clear timeout if user leaves before 1 second
                                                        if (hoverTimeout) {
                                                            clearTimeout(hoverTimeout);
                                                            setHoverTimeout(null);
                                                        }
                                                        if (!isPinned || !pinnedSegments.some(p => getSegmentKey(p.segment) === segmentKey)) {
                                                            setHoveredSegment(null);
                                                        }
                                                    }}
                                                    onClick={(e) => {
                                                        if (!isDragging && !draggingSegment && e.target.closest('.schedule-segment-resize-handle') === null) {
                                                            e.stopPropagation();
                                                            const existingPinIndex = pinnedSegments.findIndex(p => getSegmentKey(p.segment) === segmentKey);
                                                            if (existingPinIndex >= 0) {
                                                                // Unpin
                                                                setPinnedSegments(pinnedSegments.filter((_, i) => i !== existingPinIndex));
                                                                if (hoveredSegment && getSegmentKey(hoveredSegment) === segmentKey) {
                                                                    setHoveredSegment(null);
                                                                }
                                                            } else {
                                                                // Pin
                                                                const rect = e.currentTarget.getBoundingClientRect();
                                                                const newPin = {
                                                                    segment,
                                                                    position: {
                                                                        x: rect.left + rect.width / 2,
                                                                        y: rect.top - 10
                                                                    }
                                                                };
                                                                setPinnedSegments([...pinnedSegments, newPin]);
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <div className="schedule-segment-resize-handle schedule-segment-resize-left"
                                                        onMouseDown={(e) => {
                                                            e.stopPropagation();
                                                            handleResizeStart(e, segment, 'left');
                                                        }}
                                                    />
                                                    <div className="schedule-segment-content">
                                                        <div className="schedule-segment-timer-label">
                                                            {timerLabel}
                                                        </div>
                                                        <div className="schedule-segment-duration">
                                                            {formatDuration(duration)}
                                                        </div>
                                                        <div className="schedule-segment-time-range">
                                                            {formatTime(segment.startSec)} - {formatTime(segment.endSec)}
                                                        </div>
                                                    </div>
                                                    <div className="schedule-segment-resize-handle schedule-segment-resize-right"
                                                        onMouseDown={(e) => {
                                                            e.stopPropagation();
                                                            handleResizeStart(e, segment, 'right');
                                                        }}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    </div>
                </div>
            </div>
            
            {/* Step Popups - Hover and Pinned */}
            {!draggingSegment && (
                <>
                    {/* Hover popup (only one at a time) */}
                    {hoveredSegment && !pinnedSegments.some(p => getSegmentKey(p.segment) === getSegmentKey(hoveredSegment)) && (() => {
                        const segment = hoveredSegment;
                        const segmentKey = getSegmentKey(segment);
                        const recipe = recipes.find(r => r.segments.some(s => getSegmentKey(s) === segmentKey));
                        const stepText = getStepText(segment.recipeId, segment.stepIndex ?? 0);
                        const stepTitle = stepText || segment.stepTitle || `Step ${(segment.stepIndex ?? 0) + 1}`;
                        const isActive = segment.type === 'active';
                        const duration = segment.endSec - segment.startSec;
                        const timerLabel = segment.label || (isActive ? 'Active' : 'Inactive');
                        
                        // Get all segments for this step to show full step content
                        const stepSegments = recipe?.segments?.filter(s => (s.stepIndex ?? 0) === (segment.stepIndex ?? 0)) || [];
                        const stepStartTime = stepSegments.length > 0 ? Math.min(...stepSegments.map(s => s.startSec)) : segment.startSec;
                        const stepEndTime = stepSegments.length > 0 ? Math.max(...stepSegments.map(s => s.endSec)) : segment.endSec;
                        const stepDuration = stepEndTime - stepStartTime;
                        
                        return (
                            <div
                                key={`hover-${segmentKey}`}
                                className="fixed z-50 bg-neutral-0 p-4 hairline shadow-lg max-w-md pointer-events-none"
                                style={{
                                    left: `${hoverPopupPosition.x}px`,
                                    top: `${hoverPopupPosition.y}px`,
                                    transform: 'translate(-50%, -100%)',
                                    marginTop: '-8px'
                                }}
                            >
                                <div className="mb-3">
                                    <h3 className="font-sans text-xl font-medium text-[#1b263b] mb-1">
                                        {recipe?.recipeName || segment.recipeName}
                                    </h3>
                                    <p className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-2">
                                        Step {(segment.stepIndex ?? 0) + 1}
                                    </p>
                                </div>
                                
                                <div className="mb-3">
                                    <p className="font-sans text-base text-neutral-900 leading-relaxed">
                                        {stepTitle}
                                    </p>
                                </div>
                                
                                <div className="flex gap-4 font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60">
                                    <div>
                                        <span className="block mb-1">Type:</span>
                                        <span className="font-sans normal-case">{timerLabel}</span>
                                    </div>
                                    <div>
                                        <span className="block mb-1">Duration:</span>
                                        <span className="font-sans normal-case">{formatDuration(stepDuration)}</span>
                                    </div>
                                    <div>
                                        <span className="block mb-1">Time:</span>
                                        <span className="font-sans normal-case">{formatTime(stepStartTime)} - {formatTime(stepEndTime)}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                    
                    {/* Pinned popups (multiple allowed) */}
                    {pinnedSegments.map((pin, index) => {
                        const segment = pin.segment;
                        const segmentKey = getSegmentKey(segment);
                        const recipe = recipes.find(r => r.segments.some(s => getSegmentKey(s) === segmentKey));
                        const stepText = getStepText(segment.recipeId, segment.stepIndex ?? 0);
                        const stepTitle = stepText || segment.stepTitle || `Step ${(segment.stepIndex ?? 0) + 1}`;
                        const isActive = segment.type === 'active';
                        const duration = segment.endSec - segment.startSec;
                        const timerLabel = segment.label || (isActive ? 'Active' : 'Inactive');
                        
                        // Get all segments for this step to show full step content
                        const stepSegments = recipe?.segments?.filter(s => (s.stepIndex ?? 0) === (segment.stepIndex ?? 0)) || [];
                        const stepStartTime = stepSegments.length > 0 ? Math.min(...stepSegments.map(s => s.startSec)) : segment.startSec;
                        const stepEndTime = stepSegments.length > 0 ? Math.max(...stepSegments.map(s => s.endSec)) : segment.endSec;
                        const stepDuration = stepEndTime - stepStartTime;
                        
                        const isDraggingThis = draggingPopup === segmentKey;
                        
                        return (
                            <div
                                key={`pinned-${segmentKey}-${index}`}
                                className="fixed z-50 bg-neutral-0 p-4 hairline shadow-lg max-w-md cursor-move"
                                style={{
                                    left: `${pin.position.x}px`,
                                    top: `${pin.position.y}px`,
                                    transform: 'translate(-50%, -100%)',
                                    marginTop: '-8px'
                                }}
                                onMouseDown={(e) => {
                                    if (e.target.closest('button') === null) {
                                        setDraggingPopup(segmentKey);
                                        setDragStartPos({
                                            x: e.clientX - pin.position.x,
                                            y: e.clientY - pin.position.y
                                        });
                                    }
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <h3 className="font-sans text-xl font-medium text-[#1b263b] mb-1">
                                            {recipe?.recipeName || segment.recipeName}
                                        </h3>
                                        <p className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60">
                                            Step {(segment.stepIndex ?? 0) + 1}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setPinnedSegments(pinnedSegments.filter((_, i) => i !== index));
                                        }}
                                        className="text-neutral-900 hover:opacity-60 transition-opacity font-mono text-lg border-none bg-transparent p-0 m-0"
                                        style={{ color: '#0D0D0D' }}
                                    >
                                        ×
                                    </button>
                                </div>
                                
                                <div className="mb-3">
                                    <p className="font-sans text-base text-neutral-900 leading-relaxed">
                                        {stepTitle}
                                    </p>
                                </div>
                                
                                <div className="flex gap-4 font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60">
                                    <div>
                                        <span className="block mb-1">Type:</span>
                                        <span className="font-sans normal-case">{timerLabel}</span>
                                    </div>
                                    <div>
                                        <span className="block mb-1">Duration:</span>
                                        <span className="font-sans normal-case">{formatDuration(stepDuration)}</span>
                                    </div>
                                    <div>
                                        <span className="block mb-1">Time:</span>
                                        <span className="font-sans normal-case">{formatTime(stepStartTime)} - {formatTime(stepEndTime)}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </>
            )}
        </div>
    );
};

export default Schedule;

